import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "./supabase";
import { addCartToWallet } from "./PackagesManager";
import { settleVisitAppointment } from "./visitAppointment";
import { loadPaymentSettings, cardFeeFor, cardFeePct, feeLabel } from "./paymentSettings";

const STRIPE_PUBLISHABLE_KEY = "pk_live_51TPSNN9gc0MT8Jg7aFesfPT0xlWbQ5fx1kHIpHqw1nwna52Z0sai0h5L0nHbQfO9kRII1On3Ag4ikZwO6zCJ9vuc00FIGFrxoh";
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
const SUPABASE_URL = "https://fuoejykgpnfphrvqjuub.supabase.co";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  text:"#1a1a2e", muted:"#6b7280", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

async function callStripeFunction(action, params = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(SUPABASE_URL + "/functions/v1/stripe-payment", {
    method: "POST",
    headers: {
      "Authorization": "Bearer " + session.access_token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action, ...params }),
  });
  return response.json();
}

// Same auth wrapper but pointed at the Terminal Edge Function (Internet
// readers like the Stripe Reader S700). Lives next to callStripeFunction
// so the rest of the file can call them symmetrically.
async function callTerminalFunction(action, params = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(SUPABASE_URL + "/functions/v1/stripe-terminal", {
    method: "POST",
    headers: {
      "Authorization": "Bearer " + session.access_token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action, ...params }),
  });
  return response.json();
}

function PaymentForm({ clientSecret, onSuccess, onError, paymentIntentId, client, amount, description, metadata }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (error) {
      onError(error.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      const result = await callStripeFunction("record_payment", {
        payment_intent_id: paymentIntent.id,
        client_id: client?.id,
        client_name: client?.name,
        description: description,
        metadata: metadata || null,
      });
      
      if (result.success) {
        onSuccess(result.payment);
      } else {
        onError("Payment recorded failed: " + (result.error || ""));
      }
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <button type="submit" disabled={!stripe || processing} style={{marginTop:16,width:"100%",padding:"12px 20px",background:processing?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:processing?C.muted:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:processing?"not-allowed":"pointer",fontFamily:F}}>
        {processing ? "Processing..." : "Pay $" + amount}
      </button>
    </form>
  );
}

export function ChargeModal({ client, defaultAmount, description, appointmentId, onClose, onComplete }) {
  // step transitions:
  //   amount → method → (pay | terminal) → success
  // "method" is the new picker for "type card by hand" vs "use the S700".
  const [step, setStep] = useState("amount");
  const [services, setServices] = useState([]);
  const [cart, setCart] = useState([]); // [{id, name, price, qty}]
  const [serviceSearch, setServiceSearch] = useState("");
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [discountType, setDiscountType] = useState("amount"); // 'amount' | 'percent'
  const [discountValue, setDiscountValue] = useState(0);
  const [customAmount, setCustomAmount] = useState(defaultAmount || 0);
  const [useCustom, setUseCustom] = useState(!!defaultAmount && defaultAmount > 0);
  const [desc, setDesc] = useState(description || "");
  const [saveCard, setSaveCard] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [chargeMetadata, setChargeMetadata] = useState(null); // cart_items+card_fee for the payment row → wallet
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Terminal flow state
  const [readers, setReaders] = useState([]);
  const [readerId, setReaderId] = useState(localStorage.getItem("elyon_default_reader_id") || "");
  const [readersLoading, setReadersLoading] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState("");      // free-form status text
  const [terminalPolling, setTerminalPolling] = useState(false);
  const [terminalResult, setTerminalResult] = useState(null);    // {card_brand, card_last4, ...}

  const [paySettings, setPaySettings] = useState(null);
  const [savedCard, setSavedCard] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("services").select("id,name,category,price").eq("active", true).order("category").order("name");
      setServices(data || []);
    })();
    loadPaymentSettings().then(setPaySettings);
  }, []);

  // Load the client's card on file so the POS can charge it without re-keying.
  // Falls back to Stripe when saved_payment_methods wasn't mirrored locally
  // (a card attached via an older flow / failed DB write — the exact gap that
  // hid Lauren Matarazzo's card-on-file from "Take Payment").
  useEffect(() => {
    if (!client?.id) { setSavedCard(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("saved_payment_methods")
        .select("*").eq("client_id", client.id)
        .order("is_default", { ascending: false }).limit(1);
      let card = (data && data[0]) || null;
      if (!card) {
        try {
          const { data: live } = await supabase.functions.invoke("stripe-membership", {
            body: { action: "list_payment_methods", client_id: client.id },
          });
          const m = (live?.methods || []).find(x => x.is_default) || (live?.methods || [])[0];
          if (m) {
            // Mirror the Stripe card into saved_payment_methods so charge_saved_card
            // (which reads the DB, not Stripe) can actually charge it — otherwise
            // the button would show but the charge would 400 "No saved card".
            try {
              await supabase.functions.invoke("stripe-membership", {
                body: { action: "save_payment_method", client_id: client.id, payment_method_id: m.id, set_default: true },
              });
            } catch (_) { /* best-effort backfill */ }
            card = { card_brand: m.brand, card_last4: m.last4 };
          }
        } catch (_) { /* offline — just don't show a card-on-file option */ }
      }
      if (!cancelled) setSavedCard(card);
    })();
    return () => { cancelled = true; };
  }, [client?.id]);

  // Calculations
  const cartSubtotal = cart.reduce((s, i) => s + (+i.price * +i.qty), 0);
  const subtotal = useCustom ? +customAmount : cartSubtotal;
  const discountAmount = discountType === "percent"
    ? subtotal * (+discountValue / 100)
    : +discountValue;
  const total = Math.max(0, subtotal - discountAmount);
  // ★ Card processing fee — added on top of the total. 2.9% when keyed in by
  //   hand (card-not-present), 2.7% on the physical terminal (card-present).
  //   `present` is decided by which method the user picks (manual vs terminal).
  const manualFee = cardFeeFor(total, false, paySettings);
  const terminalFee = cardFeeFor(total, true, paySettings);

  const filteredServices = services.filter(s =>
    !serviceSearch || s.name.toLowerCase().includes(serviceSearch.toLowerCase()) || (s.category||"").toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const addToCart = (svc) => {
    const existing = cart.find(i => i.id === svc.id);
    if (existing) {
      setCart(cart.map(i => i.id === svc.id ? {...i, qty: i.qty + 1} : i));
    } else {
      setCart([...cart, { id: svc.id, name: svc.name, price: +svc.price || 0, qty: 1 }]);
    }
    setShowServicePicker(false);
    setServiceSearch("");
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) {
      setCart(cart.filter(i => i.id !== id));
    } else {
      setCart(cart.map(i => i.id === id ? {...i, qty} : i));
    }
  };

  const removeFromCart = (id) => setCart(cart.filter(i => i.id !== id));

  // Edit a cart line's per-unit price (e.g. IV $250 → $200). Flows to the wallet
  // via metadata.cart_items, which the auto-wallet trigger reads as unit_value.
  const updatePrice = (id, price) => setCart(cart.map(i => i.id === id ? { ...i, price: Math.max(0, price) } : i));

  const finalDescription = () => {
    const itemsList = cart.map(i => `${i.qty}× ${i.name}`).join(", ");
    return desc || itemsList || "Payment for " + (client?.name || "client");
  };

  const sharedMetadata = () => ({
    cart_items: JSON.stringify(cart),
    subtotal: subtotal.toFixed(2),
    discount_type: discountType,
    discount_value: String(discountValue),
    discount_amount: discountAmount.toFixed(2),
  });

  // The amount step's Continue button now just opens the method picker.
  // The real PaymentIntent is created only after the user chooses between
  // manual card entry and the physical Terminal reader.
  const goToMethodPicker = () => {
    if (total < 0.5) { setError("Total must be at least $0.50"); return; }
    setError("");
    setStep("method");
  };

  const startManualPayment = async () => {
    setLoading(true);
    setError("");
    // Fetch the fee config FRESH at charge time — relying on the paySettings
    // state risked a 0 fee if the async load hadn't resolved yet.
    const s = await loadPaymentSettings();
    const fee = cardFeeFor(total, false, s); // manual = card-not-present
    const grand = +(total + fee).toFixed(2);
    const meta = { ...sharedMetadata(), card_fee: fee.toFixed(2), card_fee_pct: String(cardFeePct(false, s)), base_amount: total.toFixed(2) };
    setChargeMetadata(meta); // ★ keep it so record_payment writes it onto the payment row → wallet uses exact prices
    const result = await callStripeFunction("create_payment_intent", {
      amount: grand,
      client_id: client?.id,
      client_name: client?.name,
      description: finalDescription(),
      save_card: saveCard && !!client?.id,
      metadata: meta,
    });
    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }
    setClientSecret(result.client_secret);
    setPaymentIntentId(result.payment_intent_id);
    setStep("pay");
    setLoading(false);
  };

  // Charge the card already on file — no re-keying, no terminal. Uses the same
  // server-side charge_saved_card action as the quick-charge flow (records the
  // payment + applies the card-not-present fee + emails the receipt).
  const chargeSavedCardOnFile = async () => {
    setLoading(true);
    setError("");
    const s = await loadPaymentSettings();
    const fee = cardFeeFor(total, false, s); // card-not-present rate
    const grand = +(total + fee).toFixed(2);
    const result = await callStripeFunction("charge_saved_card", {
      client_id: client?.id,
      amount: grand,
      description: finalDescription(),
      metadata: { ...sharedMetadata(), card_fee: fee.toFixed(2), card_fee_pct: String(cardFeePct(false, s)), base_amount: total.toFixed(2) },
    });
    if (result.error) { setError(result.error); setLoading(false); return; }
    if (client?.email) {
      try {
        await supabase.functions.invoke("send-receipt", {
          body: {
            to_email: client.email, to_name: client.name, amount: grand,
            subtotal, discount: discountAmount > 0 ? discountAmount : 0, card_fee: fee,
            items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
            service: finalDescription(),
            date: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
            payment_method: "Credit Card on file",
            last4: result.payment?.card_last4 || savedCard?.card_last4 || "",
            transaction_id: result.payment?.stripe_payment_intent_id || result.payment?.id || "",
          },
        });
      } catch (e) { console.warn("Receipt email failed:", e); }
    }
    setLoading(false);
    setStep("success");
    if (onComplete) onComplete(result.payment);
  };

  const startTerminalFlow = async () => {
    setError("");
    setStep("terminal");
    setReadersLoading(true);
    const res = await callTerminalFunction("list_readers");
    setReadersLoading(false);
    if (res.error) {
      setError("Could not list readers: " + res.error);
      return;
    }
    const list = res.readers || [];
    setReaders(list);
    // Auto-pick the saved default if it's still online, otherwise the
    // first online reader.
    const online = list.filter(r => r.status === "online");
    let pick = list.find(r => r.id === readerId && r.status === "online");
    if (!pick) pick = online[0] || list[0];
    if (pick) setReaderId(pick.id);
  };

  const sendToReader = async () => {
    if (!readerId) { setError("Pick a reader first"); return; }

    // Before we even create the PaymentIntent, double-check the chosen
    // reader is actually online. The saved default could have gone offline
    // since the page loaded. If so, auto-pick another online reader and
    // tell the user what just happened.
    const chosen = readers.find(r => r.id === readerId);
    if (!chosen || chosen.status !== "online") {
      const onlineAlt = readers.find(r => r.status === "online");
      if (!onlineAlt) {
        setError("No readers are online right now. Check the S700's WiFi and try again.");
        return;
      }
      setReaderId(onlineAlt.id);
      setError(`Saved reader is offline — switched to "${onlineAlt.label}" automatically. Click "Send" again to charge.`);
      return;
    }

    setLoading(true);
    setError("");
    setTerminalStatus("Creating payment...");

    // 1. Create the card_present PaymentIntent
    //    ★ Pass save_card so the terminal flow attaches the card to the
    //      client's Stripe customer for future charges (memberships,
    //      no-show fees, refills). Without this the card is lost after
    //      the receipt — owner reported this gap with Lauren Matarazzo.
    const sT = await loadPaymentSettings();
    const tFee = cardFeeFor(total, true, sT); // terminal = card-present
    const tGrand = +(total + tFee).toFixed(2);
    const intentRes = await callTerminalFunction("create_intent", {
      amount: tGrand,
      client_id: client?.id,
      client_name: client?.name,
      description: finalDescription(),
      save_card: saveCard && !!client?.id,
      metadata: { ...sharedMetadata(), card_fee: tFee.toFixed(2), card_fee_pct: String(cardFeePct(true, sT)), base_amount: total.toFixed(2) },
    });
    if (intentRes.error) {
      setError(intentRes.error);
      setLoading(false);
      setTerminalStatus("");
      return;
    }
    setPaymentIntentId(intentRes.payment_intent_id);

    // 2. Push it to the physical reader — this is when the S700 wakes up
    setTerminalStatus("Sending to reader...");
    const procRes = await callTerminalFunction("process_on_reader", {
      reader_id: readerId,
      payment_intent_id: intentRes.payment_intent_id,
    });
    if (procRes.error) {
      setError("Reader rejected the request: " + procRes.error);
      setLoading(false);
      setTerminalStatus("");
      return;
    }

    // Remember the reader so the next charge defaults to it
    localStorage.setItem("elyon_default_reader_id", readerId);

    setTerminalStatus("Waiting for customer to tap or insert card…");
    setLoading(false);
    setTerminalPolling(true);
  };

  // Poll the PaymentIntent every 2s while the reader is waiting for the
  // customer. Stops when the intent reaches a terminal state (succeeded,
  // canceled) or when the modal goes away.
  useEffect(() => {
    if (!terminalPolling || !paymentIntentId) return;
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const r = await callTerminalFunction("get_intent_status", {
        payment_intent_id: paymentIntentId,
      });
      if (cancelled) return;

      if (r.error) {
        setError(r.error);
        setTerminalPolling(false);
        return;
      }
      if (r.status === "succeeded") {
        setTerminalPolling(false);
        setTerminalResult(r);
        // ★ Create the appointment FIRST so we can pass appointment_id +
        // tip to record_payment, which writes the tip onto the appointment
        // for payroll.
        let createdApptId = null;
        if (client?.id && cart.length > 0) {
          const treatmentName = cart.length === 1 ? cart[0].name : cart.map(i => i.name).join(" + ");
          // Reuse today's existing appointment (keeps the client on their
          // provider's schedule) instead of creating a provider-less ghost.
          createdApptId = await settleVisitAppointment({
            clientId: client.id,
            clientName: client.name,
            treatmentName,
            total: r.subtotal ?? total,
            notes: cart.length > 1 ? "Multi-service: " + cart.map(i => `${i.qty}× ${i.name} ($${i.price})`).join(", ") + (discountAmount > 0 ? ` | Discount: -$${discountAmount.toFixed(2)}` : "") : "",
          });
          // ★ Carry the Stripe tip onto the appointment (payroll reads it).
          if (createdApptId && (r.tip || 0) > 0) {
            try { await supabase.from("appointments").update({ tip: r.tip }).eq("id", createdApptId); }
            catch (e) { console.warn("Could not set tip on appointment:", e); }
          }
        }
        // Record into our payments table and finish
        const rec = await callTerminalFunction("record_payment", {
          payment_intent_id: r.payment_intent_id,
          client_id: client?.id,
          client_name: client?.name,
          description: finalDescription(),
          amount: r.amount,
          card_brand: r.card_brand,
          card_last4: r.card_last4,
          charge_id: r.charge_id,
          receipt_url: r.receipt_url,
          appointment_id: createdApptId,
          // ★ Price per line, so invoices/receipts itemize terminal sales.
          cart_items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
          discount_amount: discountAmount > 0 ? +discountAmount.toFixed(2) : 0,
          subtotal: +Number(subtotal).toFixed(2),
        });
        if (rec.error) {
          setError("Charge succeeded but recording failed: " + rec.error);
          return;
        }
        // Wallet add lives in handleSuccess so it fires for both terminal
        // AND manual-card flows (both route through handleSuccess).
        await handleSuccess({ ...rec.payment, _createdApptId: createdApptId, _tip: r.tip || 0, _paymentMethod: "card-terminal" });
        return;
      }
      if (r.status === "canceled") {
        setTerminalPolling(false);
        setError(r.last_payment_error || "Customer canceled at the reader.");
        return;
      }
      // ★ DECLINE-MESSAGE FIX — owner reported "Card declined" showing
      //   every time, even when the charge actually went through.
      //
      //   `requires_payment_method` is BOTH:
      //     (a) the initial waiting state (no card presented yet), AND
      //     (b) the state after a failed attempt (reader re-prompts).
      //
      //   The previous code couldn't tell them apart — any poll after the
      //   first triggered the decline message, so customers saw "Card
      //   declined" before they'd even tapped.
      //
      //   Stripe DOES distinguish: a real decline populates
      //   `intent.last_payment_error.message`. So now we only show the
      //   decline banner when that field is actually set.
      if (r.status === "requires_payment_method") {
        if (r.last_payment_error) {
          setError(r.last_payment_error);
        } else {
          // Initial waiting state — clear any stale message left from
          // a previous polling cycle so the screen reads "Waiting for
          // customer to tap or insert card…" cleanly.
          setError("");
        }
      }
      // Card was presented and is being authorised — clear any prior
      // "declined" message; if the new attempt also fails, the next
      // requires_payment_method poll will set it again.
      if (r.status === "processing") {
        setError("");
      }
      // Safety net: after ~3 minutes, stop polling.
      if (attempts > 90) {
        setTerminalPolling(false);
        setError("Timed out waiting for the reader. Try again.");
      }
    };

    const interval = setInterval(tick, 2000);
    tick();
    return () => { cancelled = true; clearInterval(interval); };
  }, [terminalPolling, paymentIntentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelTerminal = async () => {
    setTerminalPolling(false);
    setTerminalStatus("");
    setError("");
    if (paymentIntentId || readerId) {
      await callTerminalFunction("cancel_action", {
        reader_id: readerId || undefined,
        payment_intent_id: paymentIntentId || undefined,
      });
    }
    setPaymentIntentId(null);
    setStep("method");
  };

  const handleSuccess = async (payment) => {
    // Appointment was already created above with the tip baked in for
    // terminal payments. For non-terminal flows (manual entry, etc.) we'd
    // need a different path — currently this modal is terminal-only.

    // Auto-send email receipt for every Stripe charge (terminal + manual).
    // Mirrors the cash/check flow in RecordPaymentModal so EVERY transaction
    // produces a receipt in the client's inbox without anyone clicking
    // "Send Receipt" by hand.
    if (client?.email) {
      try {
        // Receipt breakdown (owner directive 2026-08-15): itemize subtotal /
        // tip / card fee with the true charged total at the end. `total` here
        // is the cart subtotal AFTER discount — the card was charged
        // payment.amount (subtotal + tip + fee), so sending `total` alone
        // under-reported whenever a tip was added on the terminal.
        const tip = Number(payment?._tip || 0);
        const charged = Number(payment?.amount || 0) || +(total + tip).toFixed(2);
        const fee = Math.max(0, +(charged - total - tip).toFixed(2));
        await supabase.functions.invoke("send-receipt", {
          body: {
            to_email: client.email,
            to_name: client.name,
            amount: charged,
            // Pre-discount subtotal: the discount row below takes it to `total`.
            subtotal,
            tip,
            card_fee: fee,
            discount: discountAmount > 0 ? discountAmount : 0,
            items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
            service: cart.length > 0 ? cart.map(i => `${i.qty}× ${i.name}`).join(", ") : (desc || "Payment"),
            provider: payment?.provider || "",
            date: new Date().toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"}),
            payment_method: payment?.method === "terminal" ? "Credit Card (Terminal)" : "Credit Card",
            last4: payment?.card_last4 || "",
            transaction_id: payment?.stripe_payment_intent_id || payment?.id || "",
          }
        });
      } catch (e) { console.warn("Receipt email failed:", e); }
    }

    // ★ AUTO-WALLET — every paid cart item lands in the client's wallet.
    //   Owner directive (2026-06-10): "every client who pays — what she
    //   bought goes to her wallet so she can deduct as she uses sessions
    //   or swap treatments she didn't like later."
    //
    //   Skipped when there's no cart (e.g. custom-amount one-off without
    //   items), since we wouldn't know what "unit" to add to the wallet.
    if (client?.id && cart.length > 0) {
      let walletAdded = 0;
      let firstError = null;
      try {
        const { data: { user: _u } } = await supabase.auth.getUser();
        let _staffName = "Staff";
        if (_u?.id) {
          const { data: _p } = await supabase.from("profiles").select("full_name,email").eq("id", _u.id).maybeSingle();
          _staffName = _p?.full_name || _p?.email || "Staff";
        }
        const _res = await addCartToWallet(client.id, client.name, cart, {
          paymentId: payment?.id || null,
          paymentMethod: payment?._paymentMethod || "card",
          userId: _u?.id || null,
          userName: _staffName,
        });
        walletAdded = _res.added || 0;
        firstError = _res.errors?.[0] || null;
        console.log("[wallet] auto-added", walletAdded, "item(s) to wallet for", client.name, _res);
      } catch (walletErr) {
        firstError = walletErr?.message || String(walletErr);
        console.error("[wallet] auto-add failed:", walletErr);
      }
      // ★ Visible alert when nothing landed — mirrors RecordPaymentModal's
      //   behaviour so the card flow isn't silently weaker than the cash flow.
      if (walletAdded === 0) {
        alert(`⚠️ Payment succeeded but NO items reached ${client.name}'s wallet.\n\nReason: ${firstError || "Unknown — see console"}\n\nThe charge itself is fine. Add the items manually via the Wallet "+ Add Item" button.`);
      } else if (firstError) {
        alert(`⚠️ Partial wallet add for ${client.name}: ${walletAdded} of ${cart.length} items added.\n\nFirst failure: ${firstError}`);
      }
    }

    // Bump the client's lifetime spent + visit count so reports stay in sync
    // (terminal/card charges were previously skipping this — only cash via
    // RecordPaymentModal was updating these fields).
    if (client?.id && total > 0) {
      try {
        const { data: current } = await supabase
          .from("clients")
          .select("spent,visits")
          .eq("id", client.id)
          .single();
        const newSpent = Number(current?.spent || 0) + Number(total || 0);
        const newVisits = Number(current?.visits || 0) + 1;
        await supabase.from("clients").update({
          spent: newSpent,
          visits: newVisits,
          last: new Date().toISOString().split("T")[0],
        }).eq("id", client.id);
      } catch (e) {
        console.warn("Failed to bump client lifetime spent:", e);
      }
    }

    setStep("success");
    if (onComplete) onComplete(payment);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"10px",backdropFilter:"blur(4px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:540,padding:"clamp(14px, 4vw, 24px)",maxHeight:"92vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted,zIndex:10}}>×</button>

        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>💳 Take Payment</div>
        {client && <div style={{fontSize:12,color:C.muted,marginBottom:18}}>{client.name}</div>}

        {step === "amount" && (
          <div>
            {/* Toggle between cart and custom amount */}
            <div style={{display:"flex",gap:6,marginBottom:14,padding:3,background:C.light,borderRadius:8}}>
              <button onClick={()=>setUseCustom(false)} style={{flex:1,padding:"7px 10px",background:!useCustom?"#fff":"transparent",color:!useCustom?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:!useCustom?"0 1px 3px rgba(0,0,0,0.06)":"none"}}>🛒 Pick Services</button>
              <button onClick={()=>setUseCustom(true)} style={{flex:1,padding:"7px 10px",background:useCustom?"#fff":"transparent",color:useCustom?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:useCustom?"0 1px 3px rgba(0,0,0,0.06)":"none"}}>💰 Custom Amount</button>
            </div>

            {!useCustom && (
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase"}}>Services ({cart.length})</label>
                  <button onClick={()=>setShowServicePicker(true)} style={{padding:"5px 12px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add Service</button>
                </div>

                {cart.length === 0 ? (
                  <div style={{padding:18,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:8,color:C.muted,fontSize:12}}>
                    No services selected yet. Click "+ Add Service" above.
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {cart.map(item => (
                      <div key={item.id} style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:7}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:C.text}}>{item.name}</div>
                          <div style={{display:"flex",alignItems:"center",gap:4,marginTop:3}}>
                            <span style={{fontSize:11,color:C.muted}}>$</span>
                            <input type="number" step="0.01" min="0" inputMode="decimal" value={item.price}
                              onChange={e=>{ const v=parseFloat(e.target.value); updatePrice(item.id, Number.isNaN(v)?0:v); }}
                              onFocus={e=>e.target.select()}
                              style={{width:74,fontSize:12,fontWeight:600,textAlign:"right",border:"1px solid "+C.border,borderRadius:6,padding:"4px 6px",background:"#fff",color:C.text,fontFamily:F,outline:"none"}}/>
                            <span style={{fontSize:11,color:C.muted}}>each</span>
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <button onClick={()=>updateQty(item.id, item.qty - 1)} style={{minWidth:36,minHeight:36,background:"#fff",border:"1px solid "+C.border,borderRadius:8,cursor:"pointer",fontSize:18,color:C.muted,touchAction:"manipulation"}}>−</button>
                          <span style={{minWidth:28,textAlign:"center",fontSize:15,fontWeight:700}}>{item.qty}</span>
                          <button onClick={()=>updateQty(item.id, item.qty + 1)} style={{minWidth:36,minHeight:36,background:"#fff",border:"1px solid "+C.border,borderRadius:8,cursor:"pointer",fontSize:18,color:C.muted,touchAction:"manipulation"}}>+</button>
                        </div>
                        <div style={{minWidth:60,textAlign:"right",fontSize:13,fontWeight:700,color:C.gold}}>
                          ${(item.price * item.qty).toFixed(2)}
                        </div>
                        <button onClick={()=>removeFromCart(item.id)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:14,padding:"0 4px"}}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {useCustom && (
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Custom Amount ($)</label>
                <input type="number" step="0.01" inputMode="decimal" value={customAmount} onChange={e=>setCustomAmount(e.target.value)} placeholder="100.00" style={{width:"100%",padding:"12px 14px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:18,fontWeight:700,fontFamily:F,outline:"none",boxSizing:"border-box",color:C.text}} autoFocus/>
                <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Description (optional)" style={{marginTop:8,width:"100%",padding:"10px 14px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:16,fontFamily:F,outline:"none",boxSizing:"border-box",color:C.text}}/>
              </div>
            )}

            {/* Discount section */}
            {(cart.length > 0 || useCustom) && subtotal > 0 && (
              <div style={{marginBottom:14,padding:12,background:"rgba(46,139,87,0.06)",border:"1px solid "+C.green+"30",borderRadius:8}}>
                <label style={{fontSize:11,fontWeight:700,color:C.green,textTransform:"uppercase",display:"block",marginBottom:6}}>🏷 Discount (optional)</label>
                <div style={{display:"flex",gap:6}}>
                  <select value={discountType} onChange={e=>setDiscountType(e.target.value)} style={{padding:"8px 10px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:12,fontFamily:F}}>
                    <option value="amount">$ off</option>
                    <option value="percent">% off</option>
                  </select>
                  <input type="number" step="0.01" min="0" value={discountValue} onChange={e=>setDiscountValue(e.target.value)} placeholder="0" style={{flex:1,padding:"8px 12px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:13,fontWeight:600,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
                </div>
                {discountValue > 0 && (
                  <div style={{fontSize:11,color:C.green,marginTop:5,fontWeight:600}}>
                    Saving ${discountAmount.toFixed(2)} {discountType === "percent" ? `(${discountValue}% off)` : ""}
                  </div>
                )}
              </div>
            )}

            {/* Totals */}
            {(cart.length > 0 || useCustom) && (
              <div style={{padding:14,background:C.light,borderRadius:10,marginBottom:14,border:"1px solid "+C.border}}>
                {(discountValue > 0 && discountAmount > 0) && (
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.muted,marginBottom:4}}>
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.green,marginBottom:6}}>
                      <span>Discount</span>
                      <span>−${discountAmount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",paddingTop:6,borderTop:"1px solid "+C.border}}>
                  <span style={{fontSize:13,fontWeight:700,color:C.text}}>Total</span>
                  <span style={{fontSize:24,fontWeight:800,color:C.gold,fontFamily:S}}>${total.toFixed(2)}</span>
                </div>
              </div>
            )}

            {client?.id && (
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 14px",background:C.goldBg,borderRadius:8,marginBottom:14,border:"1px solid "+C.borderGold}}>
                <input type="checkbox" checked={saveCard} onChange={e=>setSaveCard(e.target.checked)} style={{accentColor:C.gold}} />
                <span style={{fontSize:12,color:C.text,fontWeight:600}}>💾 Save card for future charges</span>
              </label>
            )}

            {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}

            <button onClick={goToMethodPicker} disabled={loading || total < 0.5} style={{width:"100%",padding:"12px",background:loading||total<0.5?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:loading||total<0.5?C.muted:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:loading||total<0.5?"not-allowed":"pointer",fontFamily:F}}>
              {loading?"Loading...":`Continue → $${total.toFixed(2)}`}
            </button>
          </div>
        )}

        {/* Service Picker Modal */}
        {showServicePicker && (
          <div onClick={()=>setShowServicePicker(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:12,width:"100%",maxWidth:420,maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{padding:14,borderBottom:"1px solid "+C.border,background:C.goldBg}}>
                <input autoFocus value={serviceSearch} onChange={e=>setServiceSearch(e.target.value)} placeholder="🔍 Search services..." style={{width:"100%",padding:"12px 14px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:16,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{flex:1,overflow:"auto"}}>
                {filteredServices.length === 0 ? (
                  <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No matches</div>
                ) : filteredServices.map(s => (
                  <button key={s.id} onClick={()=>addToCart(s)} style={{width:"100%",padding:"10px 14px",border:"none",borderBottom:"1px solid "+C.border,background:"#fff",cursor:"pointer",textAlign:"left",fontFamily:F,display:"flex",justifyContent:"space-between",alignItems:"center"}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.goldBg}
                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.text}}>{s.name}</div>
                      <div style={{fontSize:10,color:C.muted}}>{s.category}</div>
                    </div>
                    <div style={{fontSize:13,fontWeight:700,color:C.gold,flexShrink:0,marginLeft:10}}>${(+s.price||0).toFixed(0)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === "method" && (
          <div>
            <div style={{padding:"10px 14px",background:C.goldBg,borderRadius:8,marginBottom:18,border:"1px solid "+C.borderGold}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:2}}>Total to charge</div>
              <div style={{fontSize:24,fontWeight:800,color:C.gold,fontFamily:S}}>${total.toFixed(2)}</div>
              {cart.length > 0 && <div style={{fontSize:11,color:C.muted,marginTop:4}}>{cart.map(i=>`${i.qty}× ${i.name}`).join(", ")}</div>}
            </div>

            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>How would you like to charge?</div>

            {savedCard && (
            <button onClick={chargeSavedCardOnFile} disabled={loading} style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"16px 18px",background:"linear-gradient(135deg,"+C.green+",#1e6b3f)",color:"#fff",border:"none",borderRadius:12,fontSize:14,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:F,marginBottom:10,textAlign:"left"}}>
              <span style={{fontSize:30}}>⚡</span>
              <span style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,marginBottom:2}}>Charge Card on File</div>
                <div style={{fontSize:11,fontWeight:500,opacity:0.9}}>{(savedCard.card_brand||"Card").toUpperCase()} •••• {savedCard.card_last4}{manualFee>0?` · +${cardFeePct(false,paySettings)}% fee → $${(total+manualFee).toFixed(2)}`:""}</div>
              </span>
              <span style={{fontSize:18}}>→</span>
            </button>
            )}

            <button onClick={startTerminalFlow} disabled={loading} style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"16px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:12,fontSize:14,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:F,marginBottom:10,textAlign:"left"}}>
              <span style={{fontSize:30}}>📟</span>
              <span style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,marginBottom:2}}>Charge on Terminal</div>
                <div style={{fontSize:11,fontWeight:500,opacity:0.9}}>Customer taps or inserts their card on the S700{terminalFee>0?` · +${cardFeePct(true,paySettings)}% fee → $${(total+terminalFee).toFixed(2)}`:""}</div>
              </span>
              <span style={{fontSize:18}}>→</span>
            </button>

            <button onClick={startManualPayment} disabled={loading} style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"16px 18px",background:"#fff",color:C.text,border:"1.5px solid "+C.border,borderRadius:12,fontSize:14,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:F,marginBottom:10,textAlign:"left"}}>
              <span style={{fontSize:30}}>💳</span>
              <span style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,marginBottom:2}}>Type Card Manually</div>
                <div style={{fontSize:11,fontWeight:500,color:C.muted}}>Enter the card number, expiry and CVC yourself{manualFee>0?` · +${cardFeePct(false,paySettings)}% fee → $${(total+manualFee).toFixed(2)}`:""}</div>
              </span>
              <span style={{fontSize:18,color:C.muted}}>→</span>
            </button>

            <button onClick={()=>setStep("amount")} disabled={loading} style={{width:"100%",padding:"10px",background:"transparent",color:C.muted,border:"none",fontSize:12,cursor:"pointer",fontFamily:F,marginTop:6}}>
              ← Back to amount
            </button>

            {loading && <div style={{textAlign:"center",fontSize:12,color:C.muted,marginTop:10}}>Loading…</div>}
            {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,marginTop:12,fontWeight:600}}>{error}</div>}
          </div>
        )}

        {step === "terminal" && (
          <div>
            <div style={{padding:"10px 14px",background:C.goldBg,borderRadius:8,marginBottom:14,border:"1px solid "+C.borderGold}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:2}}>Charging on Terminal</div>
              <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>${(total+terminalFee).toFixed(2)}</div>
              {terminalFee > 0 && <div style={{fontSize:10,color:C.muted,marginTop:1}}>${total.toFixed(2)} + {cardFeePct(true,paySettings)}% {feeLabel(paySettings)} (${terminalFee.toFixed(2)})</div>}
              {cart.length > 0 && <div style={{fontSize:11,color:C.muted,marginTop:2}}>{cart.map(i=>`${i.qty}× ${i.name}`).join(", ")}</div>}
            </div>

            {/* Reader picker — only shown until we send to the reader */}
            {!terminalPolling && !terminalResult && (
              <>
                <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Reader</div>
                {readersLoading ? (
                  <div style={{padding:14,background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.muted,textAlign:"center"}}>Loading readers…</div>
                ) : readers.length === 0 ? (
                  <div style={{padding:14,background:C.redL,border:"1px solid "+C.red+"30",borderRadius:8,fontSize:12,color:C.red}}>
                    No readers registered. Pair an S700 in Stripe Dashboard → Terminal.
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
                    {readers.map(r => (
                      <button key={r.id} onClick={()=>setReaderId(r.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:readerId===r.id?C.goldBg:"#fff",border:"1.5px solid "+(readerId===r.id?C.gold:C.border),borderRadius:8,cursor:"pointer",textAlign:"left",fontFamily:F}}>
                        <span style={{fontSize:20}}>{r.status==="online"?"🟢":"⚫"}</span>
                        <span style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{r.label}</div>
                          <div style={{fontSize:10,color:C.muted}}>{r.device_type} · {r.status}{r.serial_number?" · #"+r.serial_number:""}</div>
                        </span>
                        {readerId===r.id && <span style={{fontSize:18,color:C.gold}}>✓</span>}
                      </button>
                    ))}
                  </div>
                )}

                {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}

                <button onClick={sendToReader} disabled={loading || !readerId || readers.find(r=>r.id===readerId)?.status!=="online"} style={{width:"100%",padding:"12px",background:loading||!readerId?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:loading||!readerId?C.muted:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:loading||!readerId?"not-allowed":"pointer",fontFamily:F}}>
                  {loading?"Sending…":`Send $${(total+terminalFee).toFixed(2)} to Reader →`}
                </button>

                <button onClick={()=>setStep("method")} disabled={loading} style={{width:"100%",padding:"10px",background:"transparent",color:C.muted,border:"none",fontSize:12,cursor:"pointer",fontFamily:F,marginTop:6}}>
                  ← Back
                </button>
              </>
            )}

            {/* Waiting state — customer is at the reader */}
            {terminalPolling && (
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:54,marginBottom:14}}>📟</div>
                <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:6}}>Waiting for customer…</div>
                <div style={{fontSize:13,color:C.text,marginBottom:4}}>{terminalStatus || "Tap or insert card on the reader"}</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:20}}>Reader will time out automatically after a minute.</div>
                {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}
                <button onClick={cancelTerminal} style={{padding:"10px 20px",background:"#fff",color:C.red,border:"1.5px solid "+C.red,borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {step === "pay" && clientSecret && (
          <div>
            <div style={{padding:"10px 14px",background:C.goldBg,borderRadius:8,marginBottom:14,border:"1px solid "+C.borderGold}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:2}}>Charging</div>
              <div style={{fontSize:20,fontWeight:800,color:C.gold}}>${(total+manualFee).toFixed(2)}</div>
              {manualFee > 0 && <div style={{fontSize:10,color:C.muted,marginTop:1}}>${total.toFixed(2)} + {cardFeePct(false,paySettings)}% {feeLabel(paySettings)} (${manualFee.toFixed(2)})</div>}
              {cart.length > 0 && <div style={{fontSize:11,color:C.muted,marginTop:2}}>{cart.map(i=>`${i.qty}× ${i.name}`).join(", ")}</div>}
            </div>
            <Elements stripe={stripePromise} options={{clientSecret, locale:"en", appearance:{theme:"stripe"}}}>
              <PaymentForm
                clientSecret={clientSecret}
                paymentIntentId={paymentIntentId}
                client={client}
                amount={(total+manualFee).toFixed(2)}
                description={desc || cart.map(i=>`${i.qty}× ${i.name}`).join(", ")}
                metadata={chargeMetadata}
                onSuccess={handleSuccess}
                onError={(e)=>setError(e)}
              />
            </Elements>
            {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,marginTop:12,fontWeight:600}}>{error}</div>}
          </div>
        )}

        {step === "success" && (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:48,marginBottom:14}}>✅</div>
            <div style={{fontSize:18,fontWeight:800,color:C.green,fontFamily:S,marginBottom:6}}>Payment Successful!</div>
            <div style={{fontSize:14,color:C.text,marginBottom:4}}>${total.toFixed(2)} charged</div>
            {client && <div style={{fontSize:12,color:C.muted,marginBottom:8}}>to {client.name}</div>}
            {cart.length > 0 && <div style={{fontSize:11,color:C.muted,marginBottom:18,padding:"8px 12px",background:C.light,borderRadius:6}}>{cart.map(i=>`${i.qty}× ${i.name}`).join(", ")}</div>}
            <button onClick={onClose} style={{padding:"10px 24px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function QuickChargeSavedCard({ client, amount, description, onClose, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(true);
  const [paySettings, setPaySettings] = useState(null);
  useEffect(() => { loadPaymentSettings().then(setPaySettings); }, []);

  // ★ TIP (2026-09-28). "Charge card on file" billed base + fee and closed,
  //   never asking about a tip, so clients paying this way walked out without
  //   leaving one while terminal payers were always prompted on the device.
  //   Providers lost the tip purely because of which button the front desk
  //   pressed. Ask here too.
  const [tipPct, setTipPct] = useState(null);   // null = not chosen yet
  const [customTip, setCustomTip] = useState("");

  // Saved card = card-not-present → the 2.9% rate.
  const baseAmt = parseFloat(amount) || 0;
  const tipAmt = tipPct === "custom"
    ? Math.max(0, parseFloat(customTip) || 0)
    : +(baseAmt * ((tipPct || 0) / 100)).toFixed(2);
  // The processing fee applies to what actually runs through the card.
  const fee = cardFeeFor(baseAmt + tipAmt, false, paySettings);
  const grandTotal = +(baseAmt + tipAmt + fee).toFixed(2);

  const charge = async () => {
    setLoading(true);
    setError("");
    // Fetch fee config fresh at charge time so a not-yet-loaded state can't
    // drop the fee to 0.
    const s = await loadPaymentSettings();
    const f = cardFeeFor(baseAmt + tipAmt, false, s);
    const gt = +(baseAmt + tipAmt + f).toFixed(2);
    const result = await callStripeFunction("charge_saved_card", {
      client_id: client.id,
      amount: gt,
      description: description,
      metadata: { card_fee: f.toFixed(2), card_fee_pct: String(cardFeePct(false, s)), base_amount: baseAmt.toFixed(2), tip: tipAmt.toFixed(2) },
    });

    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }

    // Receipt email — same auto-send behavior as the regular ChargeModal.
    if (client?.email) {
      try {
        await supabase.functions.invoke("send-receipt", {
          body: {
            to_email: client.email,
            to_name: client.name,
            amount: gt,
            subtotal: baseAmt, card_fee: f, tip: tipAmt,
            service: description || "Saved card charge",
            date: new Date().toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"}),
            payment_method: "Credit Card on file",
            last4: result.payment?.card_last4 || "",
            transaction_id: result.payment?.stripe_payment_intent_id || result.payment?.id || "",
          }
        });
      } catch (e) { console.warn("Receipt email failed:", e); }
    }

    setConfirming(false);
    setLoading(false);
    if (onComplete) onComplete(result.payment);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:400,padding:24,textAlign:"center"}}>
        {confirming ? (
          <>
            <div style={{fontSize:40,marginBottom:10}}>💳</div>
            <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:8}}>Charge Saved Card?</div>
            <div style={{fontSize:13,color:C.text,marginBottom:4}}>Charging <strong>${grandTotal.toFixed(2)}</strong></div>
            {fee > 0 && <div style={{fontSize:11,color:C.muted,marginBottom:4}}>${baseAmt.toFixed(2)} + {cardFeePct(false,paySettings)}% {feeLabel(paySettings)} (${fee.toFixed(2)})</div>}
            <div style={{fontSize:11,color:C.muted,marginBottom:18}}>to {client.name}'s card on file</div>
            {description && <div style={{fontSize:11,color:C.muted,marginBottom:14,padding:"8px",background:C.light,borderRadius:6}}>{description}</div>}
            {/* Tip — asked before the card is run, not after. */}
            <div style={{textAlign:"left",marginBottom:14,padding:"12px",background:C.light,borderRadius:9,border:"1px solid "+C.border}}>
              <div style={{fontSize:11.5,fontWeight:800,color:C.text,marginBottom:8,letterSpacing:".04em"}}>ADD A TIP?</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[18,20,25].map(p => (
                  <button key={p} type="button" onClick={()=>{setTipPct(p);setCustomTip("");}} style={{
                    flex:"1 1 62px",padding:"9px 6px",borderRadius:7,cursor:"pointer",fontFamily:F,fontSize:12.5,fontWeight:700,
                    background: tipPct===p ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : "#fff",
                    color: tipPct===p ? "#fff" : C.text,
                    border:"1px solid "+(tipPct===p ? C.goldD : C.border),
                  }}>{p}%<div style={{fontSize:10,fontWeight:600,opacity:.85}}>{"$"+(baseAmt*p/100).toFixed(2)}</div></button>
                ))}
                <button type="button" onClick={()=>setTipPct("custom")} style={{
                  flex:"1 1 62px",padding:"9px 6px",borderRadius:7,cursor:"pointer",fontFamily:F,fontSize:12.5,fontWeight:700,
                  background: tipPct==="custom" ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : "#fff",
                  color: tipPct==="custom" ? "#fff" : C.text,
                  border:"1px solid "+(tipPct==="custom" ? C.goldD : C.border),
                }}>Custom</button>
                <button type="button" onClick={()=>{setTipPct(0);setCustomTip("");}} style={{
                  flex:"1 1 62px",padding:"9px 6px",borderRadius:7,cursor:"pointer",fontFamily:F,fontSize:12.5,fontWeight:700,
                  background: tipPct===0 ? C.border : "#fff",
                  color: tipPct===0 ? C.text : C.muted,
                  border:"1px solid "+C.border,
                }}>No tip</button>
              </div>
              {tipPct==="custom" && (
                <input type="number" min="0" step="0.01" autoFocus value={customTip}
                  onChange={e=>setCustomTip(e.target.value)} placeholder="Tip amount"
                  style={{width:"100%",marginTop:8,padding:"9px 11px",border:"1px solid "+C.border,borderRadius:7,fontSize:13,fontFamily:F,boxSizing:"border-box"}} />
              )}
              {tipAmt > 0 && (
                <div style={{fontSize:11.5,color:C.green,fontWeight:700,marginTop:8}}>{"Tip $"+tipAmt.toFixed(2)+" → provider"}</div>
              )}
            </div>

            {error && <div style={{padding:"10px",background:C.redL,color:C.red,borderRadius:8,fontSize:11,marginBottom:12,fontWeight:600}}>{error}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={onClose} disabled={loading} style={{flex:1,padding:"10px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={charge} disabled={loading || tipPct === null} title={tipPct===null ? "Choose a tip option first" : ""} style={{flex:1,padding:"10px",background:(loading||tipPct===null)?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:(loading||tipPct===null)?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:(loading||tipPct===null)?"not-allowed":"pointer",fontFamily:F}}>{loading?"Charging...":tipPct===null?"Choose a tip":"Yes, Charge"}</button>
            </div>
          </>
        ) : (
          <>
            <div style={{fontSize:40,marginBottom:10}}>✅</div>
            <div style={{fontSize:16,fontWeight:800,color:C.green,fontFamily:S,marginBottom:6}}>Charged!</div>
            <div style={{fontSize:13,color:C.text,marginBottom:16}}>${grandTotal.toFixed(2)} successful</div>
            <button onClick={onClose} style={{padding:"10px 24px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentHistory({ client }) {
  const [payments, setPayments] = useState([]);
  const [savedCard, setSavedCard] = useState(null);

  useEffect(() => {
    if (!client?.id) return;
    load();
    const ch = supabase.channel("payments-"+client.id)
      .on("postgres_changes", { event: "*", schema: "public", table: "payments", filter: "client_id=eq."+client.id }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "saved_payment_methods", filter: "client_id=eq."+client.id }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [client?.id]);

  const load = async () => {
    const { data: pays } = await supabase.from("payments").select("*").eq("client_id", client.id).order("created_at", { ascending: false });
    setPayments(pays || []);
    const { data: card } = await supabase.from("saved_payment_methods").select("*").eq("client_id", client.id).eq("is_default", true).maybeSingle();
    setSavedCard(card);
  };

  const removeCard = async () => {
    if (!window.confirm("Remove saved card from this client?")) return;
    await callStripeFunction("delete_card", { saved_method_id: savedCard.id });
    load();
  };

  // ★ Same refund path as everywhere else (refund-payment): it finds the
  //   Stripe charge even for membership payments, refuses instead of faking
  //   a refund it can't send, and records the refund row (2026-10-22).
  const refund = async (payment) => {
    if (!window.confirm(`Refund $${payment.amount} from this payment?`)) return;
    const { data, error } = await supabase.functions.invoke("refund-payment", {
      body: { payment_id: payment.id, amount: +payment.amount, notify: true },
    });
    let msg = data?.error || error?.message;
    try { if (error?.context?.json) { const j = await error.context.json(); msg = j?.error || msg; } } catch (_) {}
    if (error || data?.ok === false) window.alert("Refund NOT done:\n\n" + (msg || "Unknown error"));
    else window.alert(`Refunded $${Number(data?.refunded_amount || payment.amount).toFixed(2)}${data?.stripe_refund_id ? " in Stripe (" + data.stripe_refund_id + ")" : ""}.`);
    load();
  };

  const total = payments.filter(p=>p.status==="succeeded" && !p.refunded).reduce((s,p)=>s+(+p.amount), 0);

  return (
    <div>
      {savedCard && (
        <div style={{padding:"12px 14px",background:C.greenL,borderLeft:"3px solid "+C.green,borderRadius:8,marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontSize:18}}>💳</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text}}>{(savedCard.card_brand||"Card").toUpperCase()} •••• {savedCard.card_last4}</div>
            <div style={{fontSize:10,color:C.muted}}>Expires {String(savedCard.card_exp_month).padStart(2,"0")}/{savedCard.card_exp_year} · Card on File</div>
          </div>
          <button onClick={removeCard} style={{padding:"5px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F}}>Remove</button>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>Payment History</div>
        <div style={{fontSize:13,fontWeight:800,color:C.green}}>${total.toFixed(2)} lifetime</div>
      </div>

      {payments.length === 0 && (
        <div style={{padding:"30px 20px",textAlign:"center",background:C.light,borderRadius:8,border:"1px dashed "+C.border,fontSize:12,color:C.muted}}>No payments yet</div>
      )}

      {payments.map(p => (
        <div key={p.id} style={{padding:"10px 14px",background:"#fff",border:"1px solid "+C.border,borderRadius:8,marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              <div style={{fontSize:14,fontWeight:700,color:p.refunded?C.muted:C.text,textDecoration:p.refunded?"line-through":"none"}}>${p.amount}</div>
              {p.refunded && <span style={{fontSize:10,padding:"1px 6px",background:C.redL,color:C.red,borderRadius:8,fontWeight:700}}>REFUNDED</span>}
              {!p.refunded && p.status==="succeeded" && <span style={{fontSize:10,padding:"1px 6px",background:C.greenL,color:C.green,borderRadius:8,fontWeight:700}}>PAID</span>}
            </div>
            {p.description && <div style={{fontSize:11,color:C.text}}>{p.description}</div>}
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>
              {new Date(p.created_at).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"})}
              {p.card_last4 && " · "+((p.card_brand||"card").toUpperCase())+" "+p.card_last4}
              {p.created_by_name && " · by "+p.created_by_name.split(" ")[0]}
            </div>
          </div>
          {!p.refunded && p.status==="succeeded" && (
            <button onClick={()=>refund(p)} style={{padding:"5px 8px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:6,fontSize:10,cursor:"pointer",fontFamily:F}}>Refund</button>
          )}
          {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" style={{padding:"5px 8px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:10,cursor:"pointer",fontFamily:F,textDecoration:"none"}}>Receipt</a>}
        </div>
      ))}
    </div>
  );
}
